package er.prateek.crossly.ui.screens.crossword

import androidx.compose.animation.AnimatedContent
import androidx.compose.animation.animateColorAsState
import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.Spring
import androidx.compose.animation.core.spring
import androidx.compose.animation.core.tween
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.togetherWith
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.unit.dp
import crossly.shared.generated.resources.Res
import crossly.shared.generated.resources.crossword_direction_across
import crossly.shared.generated.resources.crossword_direction_down
import crossly.shared.generated.resources.crossword_no_clue
import er.prateek.crossly.data.model.Clue
import er.prateek.crossly.data.model.ClueDirection
import er.prateek.crossly.ui.components.GameIconButton
import er.prateek.crossly.ui.components.IconChevron
import er.prateek.crossly.ui.theme.Spacing
import er.prateek.crossly.ui.theme.game
import er.prateek.crossly.ui.theme.mixWith
import org.jetbrains.compose.resources.stringResource

@Composable
fun directionLabel(direction: ClueDirection): String = stringResource(
    if (direction == ClueDirection.ACROSS) {
        Res.string.crossword_direction_across
    } else {
        Res.string.crossword_direction_down
    },
)

/**
 * The active clue, with step-through controls on either side.
 *
 * On touch this is the primary way to move between words without hunting for a
 * numbered square; on desktop it mirrors what the clue lists already show. The
 * text cross-fades on change so a clue swap is visible even when the player's
 * eyes are on the grid.
 */
@Composable
fun ClueBar(
    clue: Clue?,
    direction: ClueDirection,
    modifier: Modifier = Modifier,
    onPrevious: (() -> Unit)? = null,
    onNext: (() -> Unit)? = null,
    /**
     * Bumped by the caller (e.g. on every gutter-clue tap) to draw the eye up here even when
     * the tapped clue was already active and the cross-fade below has nothing to animate --
     * the pulse is the player's confirmation that this card now holds their full hint.
     */
    pulseSignal: Int = 0,
) {
    val colors = MaterialTheme.game
    val shape = RoundedCornerShape(18.dp)

    val pulseScale = remember { Animatable(1f) }
    val borderFlash = remember { Animatable(0f) }
    LaunchedEffect(pulseSignal) {
        if (pulseSignal <= 0) return@LaunchedEffect
        pulseScale.snapTo(1f)
        borderFlash.snapTo(1f)
        pulseScale.animateTo(1.035f, spring(dampingRatio = Spring.DampingRatioMediumBouncy, stiffness = Spring.StiffnessHigh))
        pulseScale.animateTo(1f, spring(dampingRatio = Spring.DampingRatioMediumBouncy, stiffness = Spring.StiffnessLow))
    }
    LaunchedEffect(pulseSignal) {
        if (pulseSignal <= 0) return@LaunchedEffect
        borderFlash.animateTo(0f, tween(500))
    }
    val borderColor by animateColorAsState(colors.cardBorder.mixWith(colors.accent, borderFlash.value), label = "clueBarFlash")

    Row(
        modifier = modifier
            .fillMaxWidth()
            .graphicsLayer { scaleX = pulseScale.value; scaleY = pulseScale.value }
            .clip(shape)
            .background(colors.card)
            .border(1.5.dp, borderColor, shape)
            .padding(horizontal = Spacing.sm, vertical = Spacing.sm),
        horizontalArrangement = Arrangement.spacedBy(Spacing.sm),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        onPrevious?.let {
            GameIconButton(onClick = it, diameter = 38.dp) { tint ->
                IconChevron(size = 16.dp, tint = tint, pointsLeft = true)
            }
        }

        AnimatedContent(
            targetState = clue,
            transitionSpec = { fadeIn() togetherWith fadeOut() },
            modifier = Modifier.weight(1f),
            label = "clue",
        ) { current ->
            Column(modifier = Modifier.heightIn(min = 38.dp), verticalArrangement = Arrangement.Center) {
                if (current == null) {
                    Text(
                        text = stringResource(Res.string.crossword_no_clue),
                        style = MaterialTheme.typography.bodyMedium,
                        color = colors.inkMuted,
                    )
                } else {
                    Text(
                        text = "${current.number} ${directionLabel(direction).uppercase()}",
                        style = MaterialTheme.typography.labelSmall,
                        color = colors.accent,
                    )
                    Text(
                        text = current.clue,
                        style = MaterialTheme.typography.titleMedium,
                        color = colors.ink,
                    )
                }
            }
        }

        onNext?.let {
            GameIconButton(onClick = it, diameter = 38.dp) { tint ->
                IconChevron(size = 16.dp, tint = tint, pointsLeft = false)
            }
        }
    }
}

/** Small badge used for the Daily marker in the header. */
@Composable
fun ClueBadge(text: String, modifier: Modifier = Modifier) {
    val colors = MaterialTheme.game
    Box(
        modifier = modifier
            .clip(RoundedCornerShape(999.dp))
            .background(colors.accent.copy(alpha = 0.15f))
            .padding(horizontal = Spacing.md, vertical = Spacing.xs),
    ) {
        Text(text = text, style = MaterialTheme.typography.labelSmall, color = colors.accent)
    }
}
